import React, { useState } from "react";
import { Box } from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useSnackbar } from "notistack";
import { useNavigate, useSearchParams, Link as RouterLink } from "react-router-dom";
import { GoogleOAuthProvider, GoogleLogin } from "@react-oauth/google";
import { logo_red } from "../../assets";
import { instructor_signup_api, instructor_google_signup_api } from "../../DAL/Instructors/Instructors";
import { google_signin_admin_api } from "../../DAL/Login/Login";
import { useContentSetting } from "../../Hooks/ContentSetting";
import { formatSubjectName, subjectsForRegion } from "../../layouts/schoolPortal/schoolPortalTheme";
import { iv, chip, STEP_COLORS, BLOBS, blobKeyframes, ink, body, brand } from "./inviteTheme";

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

/**
 * Signup for independent instructors, at /instructors/signup. A CFI who has
 * never had a school account creates a one-person school here.
 *
 * IT WEARS THE STUDENT'S INVITE PAGE, NOT THE LOGIN SCREEN. It used to be a
 * dark glass card lifted from Login.jsx, which made the instructor funnel look
 * like a different company to the student funnel it feeds. Same cream, same
 * white cards, same pill buttons as web-frontend's accept-invite. See
 * inviteTheme.js, which both this and BecomeInstructor.jsx read from.
 *
 * PITCH LEFT, FORM RIGHT. The form keeps a readable column width inside its
 * card; the page does not, because at 480px across a laptop it was a ribbon of
 * content down the middle of an empty screen.
 *
 * ARRIVING FROM /instructors/join DROPS THE BENEFITS. That page is the sell;
 * someone who has read it and clicked through has already been told what they
 * get, and repeating it is asking them to read the same claims twice.
 *
 * On success this stores auth exactly the way Login.jsx does and sends a new
 * instructor to onboarding.
 */

// Decides which syllabus the instructor and everyone they invite is taught
// against: UK CAA or FAA Part 61. Students inherit it from the group, so it
// cannot be per-student, and getting it wrong means teaching the Air
// Navigation Order to someone sitting an FAA written.
//
// UK only. Independent instructors are a UK product: in the US, XAir is sold to
// flight schools, and an instructor there teaches through their school rather
// than signing up alone. The server refuses a US group outright
// (InstructorGroupService.createIndependentInstructor); this list only stops
// the form offering something that would be refused.
const REGIONS = [
  { value: "UK", label: "United Kingdom (CAA PPL)" },
];

// One line each, and skipped entirely for anyone arriving from
// /instructors/join, because that page is the pitch and repeating it here asks
// someone to read the same four claims twice before they can reach a field.
const BENEFITS = [
  { icon: "🎁", title: "Your students get 14 days free", body: "Full access for two weeks, no card needed." },
  { icon: "🎯", title: "See where they are weak", body: "Their progress between lessons, so you know what to cover." },
  { icon: "🎧", title: "They book you at your rate", body: "You set the price and the hours. XAir takes 15% of a booking." },
];

export default function InstructorSignup() {
  const { setUserInfo } = useContentSetting();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fromInvite = searchParams.get("from") === "invite";
  const [isLoading, setIsLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showSubjects, setShowSubjects] = useState(false);
  const [form, setForm] = useState({
    firstName: "", lastName: "", email: "", password: "", phone: "", subjects: [], region: "UK",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: value,
      // Switching region swaps the subject list. Anything picked from the old
      // one is a key the new course does not have, and would be saved anyway.
      ...(name === "region"
        ? { subjects: prev.subjects.filter((sub) => subjectsForRegion(value).includes(sub)) }
        : {}),
    }));
  };

  const toggleSubject = (subject) => {
    setForm((prev) => ({
      ...prev,
      subjects: prev.subjects.includes(subject)
        ? prev.subjects.filter((s) => s !== subject)
        : [...prev.subjects, subject],
    }));
  };

  // Identical to Login.jsx's handleAuthSuccess. The portal reads
  // user_data.flightSchool on the very next render, so both keys must be
  // written before navigating.
  //
  // A brand-new instructor goes to onboarding rather than the dashboard: the
  // dashboard is entirely empty until setup is done, and the guard would send
  // them there anyway. `welcome` is false for someone who turned out to
  // already have an account and was signed in instead.
  const handleAuthSuccess = (result, welcome) => {
    setUserInfo(result.adminUser);
    localStorage.setItem("user_data", JSON.stringify(result.adminUser));
    localStorage.setItem("token", result.token);
    navigate(welcome ? "/school/onboarding" : "/school/dashboard");
  };

  const optionalFields = () => ({
    ...(form.phone.trim() ? { phone: form.phone.trim() } : {}),
    ...(form.subjects.length ? { subjects: form.subjects } : {}),
    // Always sent, never inferred. The server falls back to UK, but a silent
    // fallback is how a US instructor ends up on the wrong syllabus.
    region: form.region,
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.firstName.trim() || !form.lastName.trim() || !form.email.trim()) {
      enqueueSnackbar("Please fill in your name and email", { variant: "error" });
      return;
    }
    if (form.password.length < 8) {
      enqueueSnackbar("Your password needs at least 8 characters", { variant: "error" });
      return;
    }
    setIsLoading(true);
    try {
      const result = await instructor_signup_api({
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        email: form.email.trim().toLowerCase(),
        password: form.password,
        ...optionalFields(),
      });
      if ((result.code === 200 || result.code === 201) && result.token) {
        handleAuthSuccess(result, true);
        return;
      }
      if (result.code === 409) {
        enqueueSnackbar(result.message || "An account with that email already exists. Log in instead.", { variant: "warning" });
      } else {
        enqueueSnackbar(result.message || "Could not create your account", { variant: "error" });
      }
    } catch (err) {
      enqueueSnackbar("Could not create your account", { variant: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    setGoogleLoading(true);
    try {
      const credential = credentialResponse.credential;
      const result = await instructor_google_signup_api(credential, optionalFields());
      if ((result.code === 200 || result.code === 201) && result.token) {
        handleAuthSuccess(result, true);
        return;
      }
      if (result.code === 409) {
        // Already has an account: sign them in with the same credential.
        const login = await google_signin_admin_api(credential);
        if (login.code === 200 && login.token) {
          handleAuthSuccess(login, false);
          return;
        }
        enqueueSnackbar(login.message || "An account with that email already exists. Log in instead.", { variant: "warning" });
      } else {
        enqueueSnackbar(result.message || "Google sign-up failed", { variant: "error" });
      }
    } catch (err) {
      enqueueSnackbar("Google sign-up failed", { variant: "error" });
    } finally {
      setGoogleLoading(false);
    }
  };

  const benefits = (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mb: { xs: 4, md: 0 } }}>
      {BENEFITS.map((item, i) => {
        const c = STEP_COLORS[i % STEP_COLORS.length];
        return (
          <Box key={item.title} sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
            <Box sx={{
              width: 42, height: 42, borderRadius: "14px", background: c.soft, flexShrink: 0,
              display: "flex", alignItems: "center", justifyContent: "center", fontSize: "20px",
            }}>
              {item.icon}
            </Box>
            <Box>
              <Box sx={{ fontSize: "15.5px", fontWeight: 700, color: ink, lineHeight: 1.3, mb: 0.4 }}>
                {item.title}
              </Box>
              <Box sx={{ fontSize: "14px", color: body, lineHeight: 1.55 }}>
                {item.body}
              </Box>
            </Box>
          </Box>
        );
      })}
    </Box>
  );

  // Six stacked fields plus nine subject chips ran the card off the bottom of
  // the screen. Nothing was cut: the two pairs short enough to share a line do
  // (names, then email and phone), and the subject picker (optional here, and
  // asked for again in onboarding) starts folded, so the card ends at the
  // button rather than a hundred pixels below it. Password and region stay
  // full width, one under the other, by explicit request.
  const pairSx = {
    display: "grid",
    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
    columnGap: "12px",
  };

  const formBlock = (
    <>
      {/* The card needs its own title. Without one it opens on a Google button,
          and a button is not a label for the thing it is at the top of. */}
      <Box sx={{ fontSize: "19px", fontWeight: 800, color: ink, letterSpacing: "-0.4px", mb: 2.25 }}>
        Create your account
      </Box>

      {GOOGLE_CLIENT_ID && (
        <>
          <Box sx={{
            display: "flex", justifyContent: "center", mb: 2,
            opacity: googleLoading ? 0.6 : 1, pointerEvents: googleLoading ? "none" : "auto",
          }}>
            <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
              <GoogleLogin
                onSuccess={handleGoogleSuccess}
                onError={() => enqueueSnackbar("Google sign-up failed", { variant: "error" })}
                theme="outline"
                shape="pill"
                size="large"
                width="320"
                text="signup_with"
              />
            </GoogleOAuthProvider>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: "12px", mb: 2 }}>
            <Box sx={{ flex: 1, height: "1px", background: "rgba(0,0,0,0.1)" }} />
            <Box sx={{ fontSize: "12px", color: body }}>or with email</Box>
            <Box sx={{ flex: 1, height: "1px", background: "rgba(0,0,0,0.1)" }} />
          </Box>
        </>
      )}

      <form onSubmit={handleSubmit}>
        <Box sx={pairSx}>
          <div style={iv.fieldWrap}>
            <label style={iv.label} htmlFor="signup-first">First name</label>
            <input id="signup-first" name="firstName" value={form.firstName} onChange={handleChange} style={iv.input} required />
          </div>
          <div style={iv.fieldWrap}>
            <label style={iv.label} htmlFor="signup-last">Last name</label>
            <input id="signup-last" name="lastName" value={form.lastName} onChange={handleChange} style={iv.input} required />
          </div>
        </Box>

        <Box sx={pairSx}>
          <div style={iv.fieldWrap}>
            <label style={iv.label} htmlFor="signup-email">Email</label>
            <input id="signup-email" type="email" name="email" value={form.email} onChange={handleChange} style={iv.input} autoComplete="email" required />
          </div>
          <div style={iv.fieldWrap}>
            <label style={iv.label} htmlFor="signup-phone">
              Phone <span style={{ color: body, fontWeight: 400 }}>(optional)</span>
            </label>
            <input id="signup-phone" type="tel" name="phone" value={form.phone} onChange={handleChange} style={iv.input} autoComplete="tel" />
          </div>
        </Box>

        <div style={iv.fieldWrap}>
          <label style={iv.label} htmlFor="signup-password">Password</label>
          <div style={{ position: "relative" }}>
            <input
              id="signup-password"
              type={showPassword ? "text" : "password"}
              name="password"
              value={form.password}
              onChange={handleChange}
              style={iv.input}
              autoComplete="new-password"
              placeholder="8 characters or more"
              required
            />
            <button type="button" onClick={() => setShowPassword(!showPassword)} style={iv.eyeBtn} aria-label={showPassword ? "Hide password" : "Show password"}>
              {showPassword ? <VisibilityOff style={{ fontSize: 18 }} /> : <Visibility style={{ fontSize: 18 }} />}
            </button>
          </div>
        </div>

        {/* No "Where you teach" picker. Independent instructors are UK-only
            (see REGIONS), so it could only ever offer one answer; the form
            still sends region "UK", which is what the server requires. Put the
            picker back if another region ever gets individual instructors. */}
        {REGIONS.length > 1 && (
          <div style={iv.fieldWrap}>
            <label style={iv.label} htmlFor="signup-region">Where you teach</label>
            <select id="signup-region" name="region" value={form.region} onChange={handleChange} style={iv.input}>
              {REGIONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
            <p style={iv.hint}>Sets the syllabus for you and your students.</p>
          </div>
        )}

        <Box sx={{ mb: 2 }}>
          <Box
            component="button"
            type="button"
            onClick={() => setShowSubjects((v) => !v)}
            sx={{
              background: "none", border: "none", padding: 0, cursor: "pointer",
              fontFamily: "inherit", fontSize: "13.5px", fontWeight: 600, color: brand,
              display: "inline-flex", alignItems: "center", gap: "6px",
            }}
            aria-expanded={showSubjects}
          >
            {showSubjects ? "Hide subjects" : "Add the subjects you teach"}
            {form.subjects.length > 0 && !showSubjects && (
              <Box component="span" sx={{ color: body, fontWeight: 500 }}>
                ({form.subjects.length} selected)
              </Box>
            )}
          </Box>
          {showSubjects && (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: "8px", mt: 1.5 }}>
              {subjectsForRegion(form.region).map((subject) => (
                <button
                  key={subject}
                  type="button"
                  onClick={() => toggleSubject(subject)}
                  style={chip(form.subjects.includes(subject))}
                  aria-pressed={form.subjects.includes(subject)}
                >
                  {formatSubjectName(subject)}
                </button>
              ))}
            </Box>
          )}
        </Box>

        {/* A Box, not a plain button: iv.btnPrimary carries an &:hover rule,
            which inline styles cannot express and React would drop. */}
        <Box
          component="button"
          type="submit"
          disabled={isLoading}
          sx={{
            ...iv.btnPrimary,
            width: "100%",
            opacity: isLoading ? 0.5 : 1,
            cursor: isLoading ? "not-allowed" : "pointer",
            ...(isLoading ? { "&:hover": { transform: "none" } } : {}),
          }}
        >
          {isLoading ? "Creating your group..." : "Create my group"}
        </Box>
      </form>
    </>
  );

  return (
    <Box sx={iv.page}>
      <style>{blobKeyframes}</style>

      {BLOBS.map((b, i) => (
        <Box
          key={b.color}
          aria-hidden
          sx={{
            position: "absolute", width: b.size, height: b.size, borderRadius: "50%",
            top: b.top, left: b.left, right: b.right, bottom: b.bottom,
            background: `radial-gradient(circle, ${b.color}, transparent 70%)`,
            filter: "blur(14px)", pointerEvents: "none", zIndex: 0,
            animation: `ivFloat ${13 + i}s ease-in-out infinite`,
            animationDelay: b.delay,
          }}
        />
      ))}

      <Box sx={iv.container}>
        <Box sx={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          py: { xs: 2.5, md: 3 },
        }}>
          <img src={logo_red} alt="XAir" style={{ height: "32px" }} />
          <Box component={RouterLink} to="/login" sx={{ ...iv.footLink, fontSize: "14px", textDecoration: "none" }}>
            Log in
          </Box>
        </Box>

        <Box sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1fr) minmax(0, 470px)" },
          gap: { xs: 4, md: 7 },
          alignItems: "start",
          pt: { xs: 1, md: 2 },
          pb: 4,
          animation: "ivRise 0.5s ease both",
        }}>
          <Box>
            <Box sx={{
              display: "inline-flex", background: "rgba(192,30,46,0.08)", color: brand,
              borderRadius: "999px", padding: "7px 16px", fontSize: "12.5px", fontWeight: 700,
              letterSpacing: "0.06em", textTransform: "uppercase", mb: 2,
            }}>
              For independent instructors
            </Box>
            <Box component="h1" sx={{
              ...iv.heading,
              fontSize: { xs: "32px", sm: "40px", md: "46px" },
              mb: 1.5,
            }}>
              Run your own group on XAir
            </Box>
            <Box component="p" sx={{
              ...iv.lead,
              fontSize: { xs: "16px", md: "17px" },
              maxWidth: "440px",
              mb: { xs: 3.5, md: 4 },
            }}>
              Free to set up, about a minute. Invite the students you already teach, and let them
              book you when they want extra help.
            </Box>

            {!fromInvite && benefits}
          </Box>

          <Box sx={iv.card}>{formBlock}</Box>
        </Box>
      </Box>
    </Box>
  );
}
